import React from 'react';
// @ts-ignore
import FormikQuestion from '../formikQuestion/formikQuestion';
import { addStyleToSpecificQuestion, isDisableSpecificQuestion } from './utils';
import { ObjectOfStrings, QuestionSchema } from '../../reducers/types';
import styles from '../general/styles/generalLabels.module.scss';
import { FormikProps } from 'formik';
import { decode } from 'html-entities';
import { CurrencySchema } from '../../actions';
import {
    dateTime,
    FormGroupVariants,
    userList,
} from '../../utils/constants/formGroupVariants';
import genericStyle from '../../assets/styles/generalStyle.module.scss';
import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import tickIcon from '../../assets/images/tick.png';

const GenericParentQuestion = (props: any): JSX.Element => {
    return <>{props.children}</>;
};

GenericParentQuestion.Form = (props: PropsType): JSX.Element => {
    const {
        question,
        questionTypes,
        defaultLabelSize,
        defaultInputSize,
        specificStyleToQuestion,
        handleCurrencySelectWrapper,
        currency,
        currencyOptions,
        isDisabled = {},
        index,
        accQuestions,
        hasDynamicStyle,
        dynamicStyle,
        isReadOnlyOrEditableQuestion,
        hasInlineElements,
        inlineGroupElements,
        inlineReadOnlyElement,
        onAutoSaveDraftClaim,
    } = props;
    return (
        <>
            <div className={hasDynamicStyle ? dynamicStyle : ''}>
                <FormikQuestion
                    question={question}
                    questionTypes={questionTypes}
                    labelSize={
                        defaultLabelSize && !question.groupShape
                            ? defaultLabelSize
                            : question.groupShape
                              ? `col-sm-6 ${styles['general-label-no-description']}`
                              : 'col-sm-12'
                    }
                    inputSize={
                        defaultInputSize && !question.groupShape
                            ? defaultInputSize
                            : question.groupShape
                              ? 'col-sm-1 col-sm-offset-4'
                              : specificStyleToQuestion
                                ? addStyleToSpecificQuestion(
                                      question,
                                      specificStyleToQuestion,
                                  )
                                : 'col-sm-12'
                    }
                    handleCurrencySelectWrapper={handleCurrencySelectWrapper}
                    infoText={question.tooltip}
                    currency={currency}
                    currencyOptions={currencyOptions}
                    isDisabledQuestion={isDisableSpecificQuestion(
                        question,
                        isDisabled,
                    )}
                    key={question?.id}
                    isReadOnlyOrEditableQuestion={isReadOnlyOrEditableQuestion}
                    hasInlineElements={hasInlineElements}
                    inlineGroupElements={inlineGroupElements}
                    inlineReadOnlyElement={inlineReadOnlyElement}
                    onAutoSaveDraftClaim={onAutoSaveDraftClaim}
                />
            </div>
            {props.children}
        </>
    );
};

GenericParentQuestion.ReadOnlyForm = (
    props: ReadOnlyFormProps,
): JSX.Element => {
    const { questionTypeCode, value, options, currencyId, percentage } = props;
    let isCheckboxLabel = questionTypeCode === FormGroupVariants.checkbox;
    let isSelectList = !!(
        (questionTypeCode === FormGroupVariants.select ||
            questionTypeCode === FormGroupVariants.list ||
            questionTypeCode === FormGroupVariants[userList]) &&
        value
    );
    let isTimeFormat = questionTypeCode === FormGroupVariants.time;
    let isDateFormat =
        questionTypeCode === FormGroupVariants.date ||
        questionTypeCode === FormGroupVariants[dateTime];

    let answerList = () => {
        let selectedValuesArray = Array.isArray(value) ? value : [value];
        return (
            <div className={genericStyle['consistent-text-view']}>
                {selectedValuesArray?.map((option: any, index: number) => {
                    const foundOption = options?.find(
                        (i: { name: string; id: string }) =>
                            i.name === option || i.id === option,
                    );
                    return (
                        <span key={index}>
                            {foundOption ? foundOption.name : ''}
                            {index < selectedValuesArray.length - 1 ? ', ' : ''}
                        </span>
                    );
                })}
            </div>
        );
    };
    let answerCheckboxLabel = () => (
        <>
            {value ? (
                <img
                    src={tickIcon}
                    alt="ticked"
                    style={{ marginRight: '8px', width: '20px' }}
                />
            ) : null}
            {props.children}
        </>
    );
    let textAnswer = () => (
        <div className={styles['consistent-text-view']}>
            {value instanceof Date
                ? GENERAL_FUNCTIONS.dateFormat(value)
                : value && typeof value === 'string'
                  ? decode(value, { scope: 'strict' })
                  : questionTypeCode === 'amount' && value
                    ? GENERAL_FUNCTIONS.formatNumbers(value)
                    : value}
            {props.children}
        </div>
    );

    let currencyAnswer = () => (
        <div className={genericStyle['consistent-text-view']}>
            {GENERAL_FUNCTIONS.appendCurrencySymbolAndFormat(
                value,
                GENERAL_FUNCTIONS.getCurrencySymbol(currencyId as string),
            )}
            {props.children}
        </div>
    );

    let dateTimeAnswer = () => (
        <div>
            {isTimeFormat
                ? GENERAL_FUNCTIONS.formattedTime(value)
                : isDateFormat
                  ? GENERAL_FUNCTIONS.formattedDate(value)
                  : null}
            {props.children}
        </div>
    );

    let percentageAnswer = () => (
        <div className={genericStyle['consistent-text-view']}>
            {`${value}%`}
            {props.children}
        </div>
    );
    return (
        <>
            {isCheckboxLabel
                ? answerCheckboxLabel()
                : isSelectList
                  ? answerList()
                  : currencyId
                    ? currencyAnswer()
                    : isTimeFormat
                      ? dateTimeAnswer()
                      : percentage
                        ? percentageAnswer()
                        : textAnswer()}
        </>
    );
};

GenericParentQuestion.Body = (props: {
    children: React.ReactNode;
    dynamicStyle?: string;
    additionalStyle?: any;
    hasDynamicStyle?: boolean;
}): JSX.Element => {
    const { dynamicStyle, additionalStyle, hasDynamicStyle } = props;
    return (
        <div
            className={hasDynamicStyle ? dynamicStyle : ''}
            style={additionalStyle}
        >
            {props.children}
        </div>
    );
};

type PropsType = {
    index: number;
    accQuestions: QuestionSchema[];
    questionTypes: ObjectOfStrings;
    question: QuestionSchema;
    currency?: ObjectOfStrings | undefined;
    currencyOptions?: CurrencySchema[] | null;
    onAutoSaveDraftClaim?: (values: any) => void;
    reserveCalculationLoading?: boolean;
    handleCurrencySelectWrapper?: (
        currency: ObjectOfStrings | undefined,
        values?: any,
    ) => void;
    defaultInputSize?: string;
    defaultLabelSize?: string;
    specificStyleToQuestion?:
        { code: string; style: string; defaultStyle: string } | any;
    isHide?: string | null;
    isDisabled?: ObjectOfStrings;
    notUseFieldValidator: boolean;
    formGroupContainerStyle?: React.CSSProperties;
    hasHorizontalRule?: boolean;
    dynamicStyle?: string;
    children?: React.ReactNode;
    hasDynamicStyle?: boolean;
    formikProps?: FormikProps<any>;
    loading?: boolean | boolean[];
    isReadOnlyOrEditableQuestion?: string;
    hasInlineElements?: boolean;
    inlineGroupElements?: JSX.Element;
    inlineReadOnlyElement?: JSX.Element;
};
type ReadOnlyFormProps = {
    questionTypeCode: string;
    value?: any;
    options?: any[];
    currencyId?: string;
    percentage?: string;
    children?: React.ReactNode;
};
export default GenericParentQuestion;
